import { SplitFactory } from '@splitsoftware/splitio';
import type { IClient } from '@splitsoftware/splitio/types/splitio';

type SplitFactorySettings = {
  core: {
    authorizationKey: string;
    key: string;
  };
};

class SplitClient {
  factorySettings: SplitFactorySettings | undefined;

  client!: IClient;

  initialized: boolean;

  initializing: Promise<void> | null;

  id: string;

  constructor() {
    this.initialized = false;
    this.initializing = null;
    this.id = '';
  }

  setId(id: string) {
    if (id && id !== this.id) {
      // identifier changed - allow re-initialization with the new key.
      // Destroy the in-flight or ready client so its SDK_READY callback
      // doesn't flip our `initialized` flag for the stale identity.
      if (this.client) {
        try {
          this.client.destroy();
        } catch (err) {
          console.error('SplitClient.destroy failed during setId', err);
        }
      }
      this.id = id;
      this.initialized = false;
      this.initializing = null;
    } else if (id) {
      this.id = id;
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    if (this.initializing) {
      return this.initializing;
    }

    const authorizationKey = import.meta.env.VITE_SPLIT_CLIENT_KEY;
    if (!authorizationKey) {
      throw new Error('VITE_SPLIT_CLIENT_KEY is not configured');
    }
    if (!this.id) {
      throw new Error('SplitClient.setId must be called before initialize()');
    }

    this.factorySettings = {
      core: {
        authorizationKey,
        key: this.id,
      },
    };

    const factory = SplitFactory(this.factorySettings);
    this.client = factory.client();

    this.initializing = new Promise<void>((resolve) => {
      this.client.on(this.client.Event.SDK_READY, () => {
        this.initialized = true;
        resolve();
      });
      this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
        // SDK couldn't reach Split.io within its internal timeout - resolve so callers
        // continue, getTreatment will return 'control' and our consumers map that to
        // their default value.
        resolve();
      });
    }).catch((err) => {
      this.initializing = null;
      throw err;
    });

    return this.initializing;
  }

  isReady(): boolean {
    return this.initialized;
  }

  async treatment({
    featureFlag,
    defaultValue,
    attributes,
  }: {
    featureFlag: string;
    defaultValue: string;
    attributes?: Record<string, unknown>;
  }): Promise<{ value: string; isLoading: boolean }> {
    if (!this.initialized) {
      try {
        await this.initialize();
      } catch (err) {
        console.error('SplitClient.initialize failed', err);
        return { value: defaultValue, isLoading: true };
      }
    }
    const treatment = this.client.getTreatment(
      featureFlag,
      attributes as SplitIO.Attributes | undefined,
    );
    if (treatment === 'control') {
      return { value: defaultValue, isLoading: true };
    }
    return { value: treatment, isLoading: false };
  }
}

const splitClient = new SplitClient();
export default splitClient;
export { SplitClient as SplitSDKClient };
